import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mail, Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { base44, authClient, setActiveOrgId } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useOrganisation } from "@/lib/OrganisationContext";
import { Button } from "@/components/ui/button";
import AuthLayout from "@/components/AuthLayout";
import SignInForm from "@/components/auth/SignInForm";
import { getInvitationErrorInfo, isEmailVerificationError, isNotRecipientError } from "@/lib/invitationErrors";

export default function AcceptInvitation() {
  const { id } = useParams();
  const { isAuthenticated, isLoadingAuth, authChecked, user, checkUserAuth } = useAuth();
  const { reloadOrganisations } = useOrganisation();
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [resendStatus, setResendStatus] = useState("");
  const [resendError, setResendError] = useState("");
  const returnTo = `/accept-invitation/${encodeURIComponent(id)}`;
  const selector = useMemo(() => ({ invitation_id: id }), [id]);

  useEffect(() => {
    if (!authChecked || isLoadingAuth || !isAuthenticated) return undefined;
    let cancelled = false;
    setInviteLoading(true);
    setInviteError(null);
    authClient.organization.getInvitation({ query: { id } }).then((result) => {
      if (cancelled) return;
      if (result.error) {
        setInviteError(result.error);
        setInvite(null);
      } else {
        setInvite(result.data);
      }
    }).catch((error) => {
      if (!cancelled) setInviteError({ message: error.message });
    }).finally(() => {
      if (!cancelled) setInviteLoading(false);
    });
    return () => { cancelled = true; };
  }, [authChecked, isLoadingAuth, isAuthenticated, id]);

  async function handleAuthenticated() {
    await checkUserAuth();
  }

  async function handleRegistered(result) {
    // In production a verification-required signup deliberately has no
    // session. Keep the form mounted so the shared component can show the
    // requested/disabled delivery status. Development signups that return a
    // session can continue straight to the authenticated invitation lookup.
    if (result?.token || result?.session) {
      await checkUserAuth();
      return true;
    }
    return false;
  }

  async function handleAccept() {
    setAccepting(true);
    setAcceptError(null);
    try {
      const result = await authClient.organization.acceptInvitation({ invitationId: id });
      if (result.error) {
        setAcceptError(result.error);
        return;
      }
      const orgId = result.data?.invitation?.organizationId ?? result.data?.member?.organizationId ?? null;
      if (orgId) setActiveOrgId(orgId);
      await reloadOrganisations();
      window.location.href = "/";
    } catch (error) {
      setAcceptError({ message: error.message, code: error.code, provider_id: error.provider_id });
    } finally {
      setAccepting(false);
    }
  }

  async function handleResendVerification() {
    setResendError("");
    setResendStatus("");
    try {
      const result = await base44.auth.resendOtp(user?.email, `${window.location.origin}${returnTo}`);
      setResendStatus(result?.email_delivery === "disabled"
        ? "Your account is saved, but email delivery is disabled. Contact an administrator for verification help."
        : "Verification email requested. Check your inbox; it may take a moment to arrive.");
    } catch (error) {
      setResendError(error.message || "We couldn't request a verification email. Please try again.");
    }
  }

  async function switchAccount() {
    try { await authClient.signOut(); } catch {}
    window.location.href = returnTo;
  }

  async function startRequiredSignIn() {
    setAcceptError(null);
    try {
      const result = await base44.auth.getSignInOptions(selector);
      const method = (result?.methods || []).find((item) => item.kind !== "password");
      if (!method?.provider_id) throw Object.assign(new Error("The required organisation sign-in method is not available."), { code: "provider_not_found" });
      const started = await base44.auth.startSignIn(selector, method.provider_id, returnTo);
      if (!started?.url || started.redirect !== true) throw new Error("The sign-in provider did not return a redirect.");
      window.location.assign(started.url);
    } catch (error) {
      setAcceptError({ code: error.code, message: error.message });
    }
  }

  if (isLoadingAuth || !authChecked) {
    return <AuthLayout icon={Mail} title="Invitation"><p role="status" className="text-sm text-muted-foreground text-center">Loading…</p></AuthLayout>;
  }

  if (!isAuthenticated) {
    return (
      <AuthLayout
        icon={Mail}
        title="You've been invited"
        subtitle="Sign in to accept, or create an account if you're new"
        footer={<Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`} className="text-primary font-medium hover:underline">Use the full sign-in page instead</Link>}
      >
        <SignInForm selector={selector} returnTo={returnTo} invitation onAuthenticated={handleAuthenticated} onRegistered={handleRegistered} />
      </AuthLayout>
    );
  }

  if (inviteLoading) return <AuthLayout icon={Mail} title="Invitation"><p role="status" className="text-sm text-muted-foreground text-center">Checking your invitation…</p></AuthLayout>;

  if (inviteError) {
    const info = getInvitationErrorInfo(inviteError);
    const notRecipient = isNotRecipientError(inviteError);
    const needsVerify = isEmailVerificationError(inviteError);
    const methodRequired = inviteError?.code === "authentication_method_required";
    return (
      <AuthLayout icon={AlertTriangle} title={info.title} footer={<Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`} className="text-primary font-medium hover:underline">Back to sign in</Link>}>
        <div role="alert" className="space-y-3"><p className="text-sm text-foreground">{info.body}</p>{notRecipient && <p className="text-sm text-muted-foreground">Signed in as {user?.email}.</p>}</div>
        {notRecipient && <Button variant="outline" className="w-full h-12 mt-4" onClick={switchAccount}>Sign out and try a different account</Button>}
        {methodRequired && <Button variant="outline" className="w-full h-12 mt-4" onClick={startRequiredSignIn}><ArrowRight className="w-4 h-4" aria-hidden="true" />Continue with required organisation sign-in</Button>}
        {needsVerify && <><Button variant="outline" className="w-full h-12 mt-4" onClick={handleResendVerification} disabled={Boolean(resendStatus)}>{resendStatus ? "Verification request sent" : "Resend verification email"}</Button>{resendStatus && <p role="status" className="text-sm text-muted-foreground mt-3">{resendStatus}</p>}{resendError && <p role="alert" className="text-sm text-red-800 mt-3">{resendError}</p>}</>}
      </AuthLayout>
    );
  }

  if (!invite) return null;
  return (
    <AuthLayout icon={CheckCircle2} title="You've been invited">
      <p className="text-sm text-foreground"><strong>{invite.inviterEmail}</strong> invited you to join <strong>{invite.organizationName}</strong> as {invite.role}.</p>
      {acceptError && <div role="alert" className="mt-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm">{getInvitationErrorInfo(acceptError).body}</div>}
      {acceptError && isNotRecipientError(acceptError) && <Button variant="outline" className="w-full h-12 mt-4" onClick={switchAccount}>Sign out and try a different account</Button>}
      {acceptError?.code === "authentication_method_required" && <Button variant="outline" className="w-full h-12 mt-4" onClick={startRequiredSignIn}><ArrowRight className="w-4 h-4" aria-hidden="true" />Continue with required organisation sign-in</Button>}
      {acceptError && isEmailVerificationError(acceptError) && <><Button variant="outline" className="w-full h-12 mt-4" onClick={handleResendVerification} disabled={Boolean(resendStatus)}>{resendStatus ? "Verification request sent" : "Resend verification email"}</Button>{resendStatus && <p role="status" className="text-sm text-muted-foreground mt-3">{resendStatus}</p>}{resendError && <p role="alert" className="text-sm text-red-800 mt-3">{resendError}</p>}</>}
      {(!acceptError || (!isNotRecipientError(acceptError) && acceptError?.code !== "authentication_method_required")) && <Button className="w-full h-12 font-medium mt-6" onClick={handleAccept} disabled={accepting}>{accepting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Accepting…</> : "Accept invitation"}</Button>}
    </AuthLayout>
  );
}
